import { Button } from "@/components/ui/button";
import { PartySelect } from "@/components/ui/party-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { getQuestionOptionsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { getNonRegistrantRegistrantAccountsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { updateForeclosureCaseReview } from "@/hera-api/foreclosure-license-link-api";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import { useForeclosurePartyQuestionIds } from "@/hooks/use-foreclosure-party-question-ids";
import {
  type ForeclosurePartyField,
  foreclosurePartyDisplayValue,
  foreclosurePartyEffectiveId,
  foreclosurePartyIsOverridden,
  foreclosurePartySuggestedId,
  formatForeclosurePartyCellLabel,
} from "@/lib/foreclosure-case-party-utils";
import {
  applyForeclosureCaseReviewToCachedLists,
  invalidateForeclosureCaseChartQueries,
  partyOverrideCachePatch,
} from "@/lib/foreclosure-case-query-invalidation";
import { buildRegistrantSelectOptions } from "@/lib/registrant-display";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

interface ForeclosureCasePartyEditCellProps {
  row: GovernmentForeclosureCaseListItem;
  field: ForeclosurePartyField;
  portal: "government" | "operator";
}

export function ForeclosureCasePartyEditCell({
  row,
  field,
  portal,
}: ForeclosureCasePartyEditCellProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const display = foreclosurePartyDisplayValue(row, field);
  const isOverridden = foreclosurePartyIsOverridden(row, field);
  const effectiveId = foreclosurePartyEffectiveId(row, field);
  const suggestedId = foreclosurePartySuggestedId(row, field);
  const [draftId, setDraftId] = useState<string | null>(effectiveId);

  const { omtQuestionId, amsQuestionId } = useForeclosurePartyQuestionIds(
    row.jurisdiction_id,
    portal,
  );

  const questionId =
    field === "omt" ? omtQuestionId : field === "ams" ? amsQuestionId : null;

  const partyOptionsQuery = useQuery({
    ...getQuestionOptionsOptions({
      query: { question_id: questionId ?? undefined, include_deleted: false },
    }),
    enabled: open && field !== "registrant" && Boolean(questionId),
  });

  // All primary accounts, not only those with a license in this jurisdiction.
  const registrantsQuery = useQuery({
    ...getNonRegistrantRegistrantAccountsOptions({
      query: { include_deleted: false },
    }),
    enabled: open && field === "registrant",
  });

  const partyEntries = useMemo(
    () =>
      (partyOptionsQuery.data?.question_options ?? [])
        .filter((option) => !option.deleted_at && option.party_id)
        .map((option) => ({
          value: option.party_id as string,
          companyName: option.party?.company_name,
          contactName: option.party?.name,
          email: option.party?.email,
        })),
    [partyOptionsQuery.data?.question_options],
  );
  const registrantOptions = useMemo(
    () =>
      buildRegistrantSelectOptions(
        registrantsQuery.data?.registrant_accounts ?? [],
      ),
    [registrantsQuery.data?.registrant_accounts],
  );

  const saveMutation = useMutation({
    mutationFn: async (nextId: string | null) => {
      const overrideBody =
        field === "omt"
          ? { overrideOmtPartyId: nextId }
          : field === "ams"
            ? { overrideAmsPartyId: nextId }
            : { overrideRegistrantAccountId: nextId };
      return updateForeclosureCaseReview(portal, {
        countyFips: row.county_fips,
        caseNumber: row.case_number,
        ...overrideBody,
      });
    },
    onSuccess: (review, nextId) => {
      const nextDisplay =
        nextId == null
          ? null
          : field === "registrant"
            ? (registrantOptions.find((option) => option.value === nextId)
                ?.label ?? null)
            : (() => {
                const party = partyEntries.find(
                  (option) => option.value === nextId,
                );
                return (
                  party?.companyName?.trim() ||
                  party?.contactName?.trim() ||
                  null
                );
              })();
      applyForeclosureCaseReviewToCachedLists(
        queryClient,
        review,
        partyOverrideCachePatch(row, field, nextId, nextDisplay),
      );
      void invalidateForeclosureCaseChartQueries(queryClient);
      setOpen(false);
      toast({
        title: "Party override saved",
        variant: "secondary",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to save override",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftId(effectiveId);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="max-w-full truncate text-left underline-offset-2 hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {formatForeclosurePartyCellLabel(display, isOverridden)}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 space-y-3"
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        {field === "registrant" ? (
          <Select
            options={registrantOptions}
            value={draftId ? [draftId] : []}
            onValueChange={(value) => setDraftId(value[0] ?? null)}
            multiple={false}
            placeholder="Select REGISTRANT"
            emptyMessage="No options available"
          />
        ) : (
          <PartySelect
            parties={partyEntries}
            labelBy="company"
            value={draftId ? [draftId] : []}
            onValueChange={(value) => setDraftId(value[0] ?? null)}
            multiple={false}
            placeholder={`Select ${field.toUpperCase()}`}
            searchPlaceholder={`Search ${field.toUpperCase()} companies…`}
            emptyMessage="No options available"
          />
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate(draftId)}
          >
            Save
          </Button>
          {isOverridden && suggestedId ? (
            <Button
              size="sm"
              variant="outline"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(null)}
            >
              Reset to suggestion
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
